import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, PageShell } from '@/components/layout';
import { NavigationPill } from '@/components/home';
import {
  LensCareSection,
  PatientCareSection,
  ProductSection,
  ProductsSecondaryNav,
} from '@/components/products';
import { HERO_LINK_KEYS, LENS_CARE_SECTION, PRODUCT_SECTIONS } from './content';
import { useRegion } from '@/utils/useRegion';

const MOBILE_QUERY = '(max-width: 767px)';
const SCROLL_IDLE_MS = 2000;

export default function Products() {
  const { t } = useTranslation();
  const country = useRegion();
  const heroRef = useRef<HTMLDivElement>(null);
  const [heroOutOfView, setHeroOutOfView] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });
  const [isScrolling, setIsScrolling] = useState(false);

  // Track the active breakpoint so we can apply mobile-specific visibility
  // rules (scroll-driven) without disturbing the desktop hero-driven logic.
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    setIsMobile(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Hero observer — desktop visibility gate. `isIntersecting` is true while
  // any pixel of the hero peeks into the viewport, so its inverse marks the
  // moment the hero scrolls completely off-screen.
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroOutOfView(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Mobile-only scroll detector — flips `isScrolling` true on every scroll
  // event and resets a 3s idle timer; `isScrolling` falls false once the user
  // stops scrolling for that long. Listener stays mounted across breakpoint
  // changes (cheap + passive) so the value is correct the moment isMobile flips.
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      setIsScrolling(true);
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => setIsScrolling(false), SCROLL_IDLE_MS);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (timeout) clearTimeout(timeout);
    };
  }, []);

  const showSecondaryNav = isMobile ? isScrolling : heroOutOfView;

  const heroLinks = useMemo(
    () =>
      HERO_LINK_KEYS.map(({ key, hash }) => ({
        label: t(`products.heroLinks.${key}`),
        to: hash,
      })),
    [t],
  );

  return (
    <PageShell
      title={t('products.meta.title')}
      description={t('products.meta.description')}
      path="/products"
      hideHeader
    >
      <NavigationPill />
      <ProductsSecondaryNav visible={showSecondaryNav} />
      <div ref={heroRef}>
        <PageHeader title={t('products.pageHeader')} links={heroLinks} />
      </div>
      {PRODUCT_SECTIONS.map((section) => (
        <ProductSection
          key={section.id}
          section={section}
          tinted={section.id === 'color-family'}
          revealOnMount={section.id === 'clear-family'}
          prioritizeFirstImage={section.id === 'clear-family'}
        />
      ))}
      {country != 'US' && <LensCareSection section={LENS_CARE_SECTION} tinted />}
      <PatientCareSection />
    </PageShell>
  );
}
